/**
 * Generic resource admin (CRUD) — list, create, edit, delete.
 * Field configs drive forms.
 */
import { useEffect, useState, useRef } from "react";
import { api, formatErr } from "../../lib/api";
import { toast } from "sonner";
import { Plus, Edit3, Trash2, Upload, X } from "lucide-react";

export default function ResourceAdmin({ title, resource, fields, listKey = "name", emptyValues, imageField, allFlag = true, sortBy = "sort_order" }) {
    const [items, setItems] = useState([]);
    const [editing, setEditing] = useState(null); // null = closed, {} = new, item = edit
    const [loading, setLoading] = useState(false);

    const fetchItems = async () => {
        const url = allFlag ? `/${resource}?all=true` : `/${resource}`;
        const { data } = await api.get(url);
        setItems(data);
    };

    useEffect(() => { fetchItems(); }, []);

    const onSave = async (values) => {
        setLoading(true);
        try {
            if (editing.id) {
                await api.put(`/${resource}/${editing.id}`, values);
            } else {
                await api.post(`/${resource}`, values);
            }
            toast.success("Saved");
            setEditing(null);
            await fetchItems();
        } catch (err) {
            toast.error(formatErr(err));
        } finally { setLoading(false); }
    };

    const onDelete = async (id) => {
        if (!window.confirm("Delete this item?")) return;
        try {
            await api.delete(`/${resource}/${id}`);
            toast.success("Deleted");
            await fetchItems();
        } catch (err) {
            toast.error(formatErr(err));
        }
    };

    return (
        <div data-testid={`admin-${resource}`}>
            <div className="flex justify-between items-center mb-8">
                <div>
                    <div className="text-gold text-[10px] tracking-[0.4em] uppercase font-bold mb-2">Manage</div>
                    <h1 className="h-display text-4xl">{title}</h1>
                </div>
                <button onClick={() => setEditing(emptyValues || {})} data-testid={`${resource}-new-btn`} className="btn-gold px-5 py-3 text-xs inline-flex items-center gap-2">
                    <Plus size={14} /> New
                </button>
            </div>

            <div className="card-flat overflow-hidden">
                <table className="w-full text-sm">
                    <thead className="bg-bg-soft border-b border-line">
                        <tr>
                            <th className="text-left p-4 text-[10px] uppercase tracking-widest text-ink">{listKey}</th>
                            <th className="text-left p-4 text-[10px] uppercase tracking-widest text-ink">Published</th>
                            <th className="text-right p-4 text-[10px] uppercase tracking-widest text-ink">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {items.map((it) => (
                            <tr key={it.id} className="border-t border-line hover:bg-bg-soft/50">
                                <td className="p-4 flex items-center gap-3">
                                    {imageField && it[imageField] && (
                                        <img src={it[imageField]} alt="" className="w-10 h-10 object-cover" />
                                    )}
                                    <div>
                                        <div className="text-white">{it[listKey] || "—"}</div>
                                        {it.title && it[listKey] !== it.title && <div className="text-ink text-xs">{it.title}</div>}
                                    </div>
                                </td>
                                <td className="p-4 text-ink">{it.is_published === false ? "Draft" : "Yes"}</td>
                                <td className="p-4 text-right">
                                    <button onClick={() => setEditing(it)} data-testid={`${resource}-edit-${it.id}`} className="text-gold hover:text-gold-bright p-2 inline-block"><Edit3 size={14} /></button>
                                    <button onClick={() => onDelete(it.id)} data-testid={`${resource}-del-${it.id}`} className="text-red-400 hover:text-red-300 p-2 inline-block"><Trash2 size={14} /></button>
                                </td>
                            </tr>
                        ))}
                        {items.length === 0 && (
                            <tr><td colSpan={3} className="p-12 text-center text-ink">Nothing here yet.</td></tr>
                        )}
                    </tbody>
                </table>
            </div>

            {editing && (
                <ResourceForm
                    resource={resource}
                    fields={fields}
                    initial={editing}
                    onCancel={() => setEditing(null)}
                    onSave={onSave}
                    loading={loading}
                />
            )}
        </div>
    );
}

function ResourceForm({ resource, fields, initial, onCancel, onSave, loading }) {
    const [values, setValues] = useState(() => {
        const base = {};
        fields.forEach((f) => { base[f.key] = initial[f.key] ?? f.default ?? (f.type === "boolean" ? true : f.type === "number" ? 0 : ""); });
        return base;
    });

    const submit = (e) => { e.preventDefault(); onSave(values); };

    return (
        <div className="fixed inset-0 z-50 bg-black/80 grid place-items-center p-6 overflow-y-auto" onClick={onCancel}>
            <div className="card-flat p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid={`${resource}-form`}>
                <div className="flex justify-between items-center mb-6">
                    <h2 className="h-display text-2xl">{initial.id ? "Edit" : "Create"}</h2>
                    <button onClick={onCancel}><X /></button>
                </div>
                <form onSubmit={submit} className="space-y-4">
                    {fields.map((f) => (
                        <div key={f.key}>
                            <label className="text-[10px] tracking-widest uppercase text-ink font-bold">{f.label}</label>
                            {f.type === "textarea" ? (
                                <textarea rows={f.rows || 4} value={values[f.key]} onChange={(e) => setValues({ ...values, [f.key]: e.target.value })} className="w-full mt-2 px-4 py-3 text-sm" />
                            ) : f.type === "select" ? (
                                <select value={values[f.key]} onChange={(e) => setValues({ ...values, [f.key]: e.target.value })} className="w-full mt-2 px-4 py-3 text-sm">
                                    {f.options.map((o) => <option key={o.value || o} value={o.value || o}>{o.label || o}</option>)}
                                </select>
                            ) : f.type === "boolean" ? (
                                <div className="mt-2"><label className="inline-flex items-center gap-2 text-sm"><input type="checkbox" checked={!!values[f.key]} onChange={(e) => setValues({ ...values, [f.key]: e.target.checked })} /> {f.help || "Yes"}</label></div>
                            ) : f.type === "number" ? (
                                <input type="number" value={values[f.key]} onChange={(e) => setValues({ ...values, [f.key]: Number(e.target.value) })} className="w-full mt-2 px-4 py-3 text-sm" />
                            ) : f.type === "image" ? (
                                <ImageField value={values[f.key]} onChange={(v) => setValues({ ...values, [f.key]: v })} testid={`${resource}-${f.key}-image`} />
                            ) : (
                                <input value={values[f.key]} onChange={(e) => setValues({ ...values, [f.key]: e.target.value })} className="w-full mt-2 px-4 py-3 text-sm" />
                            )}
                            {f.help && f.type !== "boolean" && <div className="text-[10px] text-ink mt-1">{f.help}</div>}
                        </div>
                    ))}
                    <div className="flex gap-3 pt-2">
                        <button type="submit" disabled={loading} data-testid={`${resource}-save`} className="btn-gold px-7 py-3 text-xs disabled:opacity-60">{loading ? "Saving…" : "Save"}</button>
                        <button type="button" onClick={onCancel} className="btn-ghost-gold px-7 py-3 text-xs">Cancel</button>
                    </div>
                </form>
            </div>
        </div>
    );
}

function ImageField({ value, onChange, testid }) {
    const ref = useRef(null);
    const [uploading, setUploading] = useState(false);

    const upload = async (file) => {
        if (!file) return;
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
            const fullUrl = process.env.REACT_APP_BACKEND_URL + data.url;
            onChange(fullUrl);
            toast.success("Image uploaded");
        } catch (err) {
            toast.error(formatErr(err));
        } finally { setUploading(false); }
    };

    return (
        <div className="mt-2" data-testid={testid}>
            <div className="flex gap-3 items-start">
                {value && <img src={value} alt="" className="w-24 h-24 object-cover border border-line" />}
                <div className="flex-1 space-y-2">
                    <input value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="https://… or upload" className="w-full px-4 py-3 text-sm" />
                    <div className="flex gap-2">
                        <button type="button" onClick={() => ref.current?.click()} disabled={uploading} className="btn-ghost-gold px-3 py-2 text-[10px] inline-flex items-center gap-2">
                            <Upload size={12} /> {uploading ? "Uploading…" : "Upload"}
                        </button>
                        {value && <button type="button" onClick={() => onChange("")} className="text-red-400 text-[10px] uppercase tracking-widest">Remove</button>}
                    </div>
                    <input type="file" ref={ref} hidden accept="image/*" onChange={(e) => upload(e.target.files?.[0])} />
                </div>
            </div>
        </div>
    );
}
